import { getApiEndpoint } from '../config/api.js';
import { getAuthHeaders } from './auth.js';

export interface NewsArticle {
  title: string;
  content: string;
  source: string;
  url: string;
  publishedAt: string;
  sentiment?: 'positive' | 'negative' | 'neutral';
  significance?: number;
}

export interface MarketNewsResponse {
  articles: NewsArticle[];
  summary: string;
}

export interface StockNewsResponse {
  articles: NewsArticle[];
  symbol: string;
}

async function request<T>(url: URL): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(url.toString(), { headers });
  if (!response.ok) {
    let message = `News API error: ${response.status}`;
    try {
      const body = await response.clone().json();
      if (body?.error) message = body.error;
    } catch {
      const text = await response.text().catch(() => '');
      if (text) message = text;
    }
    throw new Error(message);
  }
  return (await response.json()) as T;
}

const clampLimit = (value?: number): number | undefined => {
  if (typeof value !== 'number') return undefined;
  if (!Number.isFinite(value)) return undefined;
  return Math.max(1, Math.min(25, Math.floor(value)));
};

export const newsApi = {
  async getMarketNews(opts?: { profileId?: string; limit?: number }): Promise<MarketNewsResponse> {
    const base = getApiEndpoint();
    const url = opts?.profileId
      ? new URL(`${base}/profile/${encodeURIComponent(opts.profileId)}/news`)
      : new URL(`${base}/news`);
    const bounded = clampLimit(opts?.limit);
    if (bounded !== undefined) {
      url.searchParams.set('limit', String(bounded));
    }
    return request<MarketNewsResponse>(url);
  },

  async getPortfolioNews(portfolioId: string, opts?: { limit?: number }): Promise<MarketNewsResponse> {
    const base = getApiEndpoint();
    const url = new URL(`${base}/news`);
    url.searchParams.set('portfolioId', portfolioId);
    const bounded = clampLimit(opts?.limit);
    if (bounded !== undefined) {
      url.searchParams.set('limit', String(bounded));
    }
    return request<MarketNewsResponse>(url);
  },

  async getStockNews(symbol: string, opts?: { profileId?: string; limit?: number }): Promise<NewsArticle[]> {
    const base = getApiEndpoint();
    const url = opts?.profileId
      ? new URL(`${base}/profile/${encodeURIComponent(opts.profileId)}/news`)
      : new URL(`${base}/news`);
    url.searchParams.set('symbol', symbol);
    const bounded = clampLimit(opts?.limit);
    if (bounded !== undefined) {
      url.searchParams.set('limit', String(bounded));
    }
    const data = await request<StockNewsResponse>(url);
    return data.articles;
  },
};

// Export perplexityApi for backward compatibility
export const perplexityApi = newsApi;
